'use client';

import { Options, RenderNode, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Block, Document, INLINES, Inline } from '@contentful/rich-text-types';
import cx from 'classnames';
import Link from 'next/link';
import { ReactNode, useMemo, type JSX } from 'react';
import { DeepNullable, DeepPartial } from 'ts-essentials';

import {
  Asset,
  BlogPost,
  ContentBanner,
  ContentCard,
  Entry,
  GlossaryTerm,
  MarketingSlug,
  Youtube,
} from '@flipster/contentful-graphql/codegen/types.generated';
import { useRouter } from '@flipster/i18n/routing';
import { Picture } from '@flipster/react/design-utils/picture';
import { css } from '@flipster/styled-system/css';
import { RichTextVariantProps, richText as richTextRecipe } from '@flipster/styled-system/recipes';
import { getNumberArr } from '@flipster/utils/num';
import { generateLinkWithQueryParams } from '@flipster/utils/url';
import webviewHandler from '@flipster/utils/webview-handler';

import { ContentCards } from './content-cards';
import { EmbeddedYoutube } from './embedded-youtube';

type BaseEntry = { __typename: string; sys: { id: string } };

// Contentful Rich Text 관련 타입 정의
type RichTextDocument = DeepNullable<
  DeepPartial<{
    json: Document;
    links: {
      assets: {
        block: Asset[];
        hyperlink: Asset[];
      };
      entries: {
        block: ((BaseEntry & { title: string; url: string }) | Youtube | ContentCard | ContentBanner)[];
        hyperlink: ((BaseEntry & { slug: string }) | BlogPost | GlossaryTerm | MarketingSlug)[];
        inline: Entry[];
      };
    };
  }>
>;

// 커스텀 노드 타입 (ContentCard 그룹용)
interface ContentCardGroupNode {
  nodeType: 'content-card-group';
  data: Record<string, unknown>;
  content: Block[];
}

// 확장된 노드 타입 (기본 노드 + 커스텀 노드)
type ExtendedNode = Block | Inline | ContentCardGroupNode;

interface Props {
  style?: RichTextVariantProps['style'];
  richTextDocument?: RichTextDocument | null;
  startHeadingLevel?: number;
  className?: string;
  imgQuality?: number;
  replaceRegex?: [string, ReactNode][];
}

const getHeadingTag = (level: number) => {
  const adjustedLevel = Math.min(Math.max(level, 1), 6); // 최소 1, 최대 6
  return `h${adjustedLevel}` as keyof JSX.IntrinsicElements; // h1 ~ h6 태그만 허용
};

const contentCardGroupNodeType = 'content-card-group';
// 연속된 ContentCard 블록들을 그룹화하는 함수
const groupConsecutiveContentCards = (document: RichTextDocument): RichTextDocument => {
  if (!document?.json?.content) return document;

  const newContent: ExtendedNode[] = [];
  let currentCardGroup: Block[] = [];

  const flushCardGroup = () => {
    if (currentCardGroup.length > 0) {
      // 연속된 ContentCard들을 하나의 특별한 블록으로 만듦
      newContent.push({
        nodeType: contentCardGroupNodeType,
        data: {},
        content: currentCardGroup,
      });
      currentCardGroup = [];
    }
  };

  for (const node of document.json.content) {
    if (!node) continue;

    if (node.nodeType === BLOCKS.EMBEDDED_ENTRY) {
      // ContentCard인지 확인
      const embeddedEntry = document.links?.entries?.block?.find(
        (entry) => entry?.sys?.id && entry.sys.id === node?.data?.target?.sys?.id,
      );

      if (embeddedEntry?.__typename === 'ContentCard') {
        currentCardGroup.push(node as Block);
        continue;
      }
    }
    // ContentCard가 아닌 경우, 현재 그룹을 플러시하고 노드 추가
    flushCardGroup();
    newContent.push(node as ExtendedNode);
  }
  // 마지막 그룹 플러시
  flushCardGroup();
  return {
    ...document,
    json: {
      ...document.json,
      content: newContent as Document['content'],
    },
  };
};

/**
 * @param startHeadingLevel number contentful의 h tag 를 어떤 h tag 로 그릴지 결정하는 값, default 1
 * @param className string
 * @param content fragment는 아래를 복붙해서 사용하시면 편합니다.
 *   {
 *     json
 *     links {
 *       entries {
 *         hyperlink {
 *           __typename
 *           sys {
 *             id
 *           }
 *           ... on GlossaryTerm {
 *             slug
 *           }
 *           ... on BlogPost {
 *             slug
 *           }
 *           ... on MarketingSlug {
 *             slug
 *           }
 *         }
 *         block {
 *           __typename
 *           sys {
 *             id
 *           }
 *           ... on Youtube {
 *             title
 *             url
 *           }
 *         }
 *       }
 *       assets {
 *         block {
 *           sys {
 *             id
 *           }
 *           title
 *           url
 *           width
 *           height
 *         }
 *       }
 *     }
 *   }
 */
const ContentfulRichText = ({
  style = 'default',
  richTextDocument,
  startHeadingLevel = 1,
  imgQuality = 60,
  replaceRegex,
  className,
}: Props) => {
  const router = useRouter();
  // rich text document를 전처리하여 연속된 ContentCard들을 그룹화
  const processedDocument = useMemo(() => {
    return richTextDocument ? groupConsecutiveContentCards(richTextDocument) : richTextDocument;
  }, [richTextDocument]);

  const options = useMemo<Options>(
    () => ({
      renderNode: {
        ...getNumberArr(5).reduce<RenderNode>((acc, cur) => {
          const key = BLOCKS[`HEADING_${cur + 1}` as keyof typeof BLOCKS];
          return {
            ...acc,
            [key]: (_node, children) => {
              const HeadingTag = getHeadingTag(startHeadingLevel + cur);
              return <HeadingTag>{children}</HeadingTag>;
            },
          };
        }, {}),
        [INLINES.HYPERLINK]: (node, children) => {
          if (node?.nodeType === 'hyperlink' && node?.data.uri.startsWith('https://flipster.io')) {
            const url = new URL(node?.data.uri);
            return <Link href={url.pathname + url.search + url.hash}>{children}</Link>;
          }
          if (node?.data.uri) {
            return (
              <a href={node?.data.uri} target='_blank' rel='noopener noreferrer'>
                {children}
              </a>
            );
          }
          return children;
        },
        [INLINES.ENTRY_HYPERLINK]: (node, children) => {
          const linkedEntry = richTextDocument?.links?.entries?.hyperlink?.find(
            (link) => link?.sys?.id && link.sys.id === node?.data.target.sys.id,
          );
          if (linkedEntry?.__typename === 'BlogPost' && linkedEntry?.slug) {
            return <Link href={`/blog/${linkedEntry.slug}`}>{children}</Link>;
          }
          if (linkedEntry?.__typename === 'GlossaryTerm' && linkedEntry?.slug) {
            return <Link href={`/glossary/${linkedEntry.slug}`}>{children}</Link>;
          }
          if (linkedEntry?.__typename === 'MarketingSlug' && linkedEntry?.slug) {
            return <Link href={`/marketing/${linkedEntry.slug}`}>{children}</Link>;
          }
          return children;
        },
        // 그룹화된 ContentCard들을 처리하는 커스텀 렌더러
        [contentCardGroupNodeType]: (node) => {
          const cards = node.content?.map((cardNode) => {
            // cardNode가 Block 타입인지 확인
            if (!cardNode || !('data' in cardNode)) return null;

            const embeddedEntry = richTextDocument?.links?.entries?.block?.find(
              (entry) => entry?.sys?.id && entry.sys.id === cardNode?.data?.target?.sys?.id,
            );
            return embeddedEntry?.__typename === 'ContentCard' ? (embeddedEntry as ContentCard) : null;
          });

          return <ContentCards cards={cards} />;
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node) => {
          const embeddedEntry = richTextDocument?.links?.entries?.block?.find(
            (entry) => entry?.sys?.id && entry.sys.id === node?.data.target.sys.id,
          );
          if (embeddedEntry?.__typename === 'Youtube') {
            return <EmbeddedYoutube title={embeddedEntry.title} url={embeddedEntry.url} />;
          }
          if (embeddedEntry?.__typename === 'ContentBanner') {
            const entry = embeddedEntry as ContentBanner;
            if (entry.alt) {
              const Banner = (
                <Picture
                  src={{ base: `${entry.mobile?.url}?fm=webp&q=80`, tablet: `${entry.desktop?.url}?fm=webp&q=80` }}
                  alt={entry.alt}
                  css={{ w: '100%', h: 'auto', mb: 0 }}
                  loading='lazy'
                />
              );
              const wrapperCss = css({
                display: 'block',
                w: '100%',
                my: { base: 20, tablet: 32 },
                rounded: 12,
                lineHeight: 0,
                overflow: 'hidden',
              });
              if (entry.url && entry.locator)
                return (
                  <button
                    type='button'
                    className={wrapperCss}
                    onClick={() => {
                      const goLink = () => {
                        const url = generateLinkWithQueryParams(
                          entry.url!,
                          new URLSearchParams(window.location.search),
                        );
                        const isExternal = url.startsWith('https');
                        if (isExternal) window.open(url, '_blank', 'noopener');
                        else router.push(url);
                      };
                      webviewHandler.postMessage(
                        {
                          action: 'link',
                          params: { page: entry.locator! },
                        },
                        () => goLink(),
                      );
                    }}
                  >
                    {Banner}
                  </button>
                );
              return <div className={wrapperCss}>{Banner}</div>;
            }
          }
          return null;
        },
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
          const embeddedAsset = richTextDocument?.links?.assets?.block?.find(
            (asset) => asset?.sys?.id && asset.sys.id === node?.data.target.sys.id,
          );
          if (embeddedAsset?.url) {
            return (
              <Picture
                src={{
                  base: `${embeddedAsset.url}?fm=webp&w=1200&q=${imgQuality}`,
                  tablet: `${embeddedAsset.url}?fm=webp&w=1600&q=${imgQuality}`,
                }}
                alt={embeddedAsset.title || 'flipster embedded image'}
                width={embeddedAsset.width || undefined}
                height={embeddedAsset.height || undefined}
                css={{ maxW: '100%', w: embeddedAsset.width || undefined, h: 'auto' }}
                loading='lazy'
              />
            );
          }
          return null;
        },
        [BLOCKS.PARAGRAPH]: (node, children) => {
          if (replaceRegex && Array.isArray(children)) {
            let change = false;
            const result = children.map((c) => {
              for (const [regex, value] of replaceRegex) {
                if (c === regex) {
                  change = true;
                  return value;
                }
              }
              return;
            });
            if (change) return result;
          }
          return <p>{children}</p>;
        },
        [BLOCKS.TABLE]: (node, children) => (
          <div
            className={css({
              w: '100%',
              border: '1px solid',
              borderColor: 'border.default.level-3',
              rounded: 6,
              overflowX: 'auto',
              mb: '1.6em',
            })}
          >
            <table>
              <tbody>{children}</tbody>
            </table>
          </div>
        ),
      },
    }),
    [imgQuality, richTextDocument, startHeadingLevel, replaceRegex, router],
  );

  const recipe = richTextRecipe({ style });

  return (
    <div className={cx(recipe, className)}>
      {documentToReactComponents(processedDocument?.json as Document, options)}
    </div>
  );
};

export default ContentfulRichText;
